/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Gaussian Blur Node
   High-performance 2-Pass Separable Gaussian Blur on GPU.
   O(2N) complexity eliminates main-thread stalls and large kernel penalties.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createMultiPassProcessor } from '../../rendering/effects/shaderProcessor';

const GAUSSIAN_PASS_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform vec2 u_direction; // (1.0, 0.0) for horizontal, (0.0, 1.0) for vertical
uniform float u_radius;

void main() {
  vec2 texOffset = (u_direction * max(u_radius, 0.001)) / u_resolution;
  
  // 9-tap Gaussian distribution weights
  const float weights[5] = float[5](0.2270270, 0.1945946, 0.1216216, 0.0540541, 0.0162162);
  
  vec4 center = texture(u_image, v_uv);
  vec3 accumColor = center.rgb * center.a * weights[0];
  float accumAlpha = center.a * weights[0];

  for (int i = 1; i < 5; i++) {
    float fi = float(i);
    vec4 s1 = texture(u_image, v_uv + texOffset * fi);
    vec4 s2 = texture(u_image, v_uv - texOffset * fi);
    accumColor += s1.rgb * s1.a * weights[i];
    accumColor += s2.rgb * s2.a * weights[i];
    accumAlpha += (s1.a + s2.a) * weights[i];
  }

  if (accumAlpha > 0.0001) {
    fragColor = vec4(accumColor / accumAlpha, accumAlpha);
  } else {
    fragColor = vec4(0.0);
  }
}
`;

export const gaussianBlurNode: NodeDefinition = {
  type: 'filter.blur',
  category: 'Filter',
  label: 'Gaussian Blur',
  description: 'Smooths image details using an efficient two-pass separable Gaussian filter',
  keywords: ['blur', 'gaussian', 'smooth', 'soften', 'filter', 'defocus'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Blurred', type: 'image' },
  ],
  params: [
    {
      id: 'radius',
      name: 'Blur Radius',
      type: 'slider',
      defaultValue: 6,
      min: 0,
      max: 40,
      step: 0.5,
      unit: 'px',
    },
  ],
  processor: createMultiPassProcessor({
    steps: [
      // Pass 1: Horizontal Gaussian pass
      {
        fragmentShader: GAUSSIAN_PASS_SHADER,
        getUniforms: (currentTex, _inputs, params, dimensions) => ({
          u_image: currentTex,
          u_resolution: [dimensions.width, dimensions.height],
          u_direction: [1.0, 0.0],
          u_radius: Number(params.radius) || 6,
        }),
      },
      // Pass 2: Vertical Gaussian pass
      {
        fragmentShader: GAUSSIAN_PASS_SHADER,
        getUniforms: (currentTex, _inputs, params, dimensions) => ({
          u_image: currentTex,
          u_resolution: [dimensions.width, dimensions.height],
          u_direction: [0.0, 1.0],
          u_radius: Number(params.radius) || 6,
        }),
      },
    ],
  }),
};
